import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const FILTER_LABELS = {
  min_width: { prefix: 'Width ≥', suffix: 'mm' },
  max_width: { prefix: 'Width ≤', suffix: 'mm' },
  min_thickness: { prefix: 'Thickness ≥', suffix: 'µm' },
  max_thickness: { prefix: 'Thickness ≤', suffix: 'µm' },
  min_quantity: { prefix: 'Qty ≥', suffix: '' },
  customer_specific: { prefix: 'Customer Specific:', suffix: '' },
  compliance_flag: { prefix: 'Compliant', suffix: '' },
};

const CORE_DIAMETER_LABELS = {
  '25': '25mm',
  '38': '38mm',
  '76': '76mm',
  '152': '152mm',
  'other': 'Other'
};

const AGE_BUCKET_LABELS = {
  '0_90': '< 3 months',
  '91_180': '3-6 months',
  '181_360': '6-12 months',
  'gt_360': '> 12 months'
};

export const ActiveFilterTags = ({ 
  filters, 
  sites = [],
  onRemoveFilter, 
  onClearAll,
  className 
}) => {
  const { t } = useTranslation();

  // Build list of active filter tags
  const tags = [];

  // Site filter
  if (filters.site_id) {
    const site = sites.find(s => s.site_id === filters.site_id);
    tags.push({
      key: 'site_id',
      label: `Site: ${site?.site_name || filters.site_id}`,
      value: filters.site_id
    });
  }

  // Country filter
  if (filters.country) {
    tags.push({
      key: 'country',
      label: `${t('filter.country', 'Country')}: ${filters.country}`,
      value: filters.country
    });
  }

  // Manufacturer filter
  if (filters.manufacturer) {
    tags.push({
      key: 'manufacturer',
      label: filters.manufacturer,
      value: filters.manufacturer
    });
  }

  // Width range
  if (filters.min_width) {
    tags.push({
      key: 'min_width',
      label: `${t('filter.width', 'Width')} ≥ ${filters.min_width}mm`,
      value: filters.min_width
    });
  }
  if (filters.max_width) {
    tags.push({
      key: 'max_width',
      label: `${t('filter.width', 'Width')} ≤ ${filters.max_width}mm`,
      value: filters.max_width
    });
  }

  // Thickness range
  if (filters.min_thickness) {
    tags.push({
      key: 'min_thickness',
      label: `${t('filter.thickness', 'Thickness')} ≥ ${filters.min_thickness}µm`,
      value: filters.min_thickness
    });
  }
  if (filters.max_thickness) {
    tags.push({
      key: 'max_thickness',
      label: `${t('filter.thickness', 'Thickness')} ≤ ${filters.max_thickness}µm`,
      value: filters.max_thickness
    });
  }

  // Core diameters (multi-select)
  if (filters.core_diameters) {
    const cores = filters.core_diameters.split(',').filter(Boolean);
    cores.forEach(core => {
      tags.push({
        key: 'core_diameters',
        label: `${t('filter.core', 'Core')}: ${CORE_DIAMETER_LABELS[core] || core}`,
        value: core,
        isMulti: true
      });
    });
  }

  // Certifications (multi-select)
  if (filters.certifications) {
    const certs = filters.certifications.split(',').filter(Boolean);
    certs.forEach(cert => {
      tags.push({
        key: 'certifications',
        label: cert,
        value: cert,
        isMulti: true
      });
    });
  }

  // Customer specific
  if (filters.customer_specific) {
    tags.push({
      key: 'customer_specific',
      label: `${t('filter.customerSpecific', 'Customer Specific')}: ${filters.customer_specific === 'yes' ? t('filter.yes', 'Yes') : t('filter.no', 'No')}`,
      value: filters.customer_specific
    });
  }

  // Compliance flag
  if (filters.compliance_flag === true) {
    tags.push({
      key: 'compliance_flag',
      label: t('filter.compliant', 'Compliant'),
      value: true
    });
  }

  // Minimum quantity
  if (filters.min_quantity) {
    const unit = filters.quantity_unit || 'sqm';
    tags.push({
      key: 'min_quantity',
      label: `${t('filter.qty', 'Qty')} ≥ ${filters.min_quantity} ${unit}`,
      value: filters.min_quantity
    });
  }

  // Age buckets (multi-select)
  if (filters.age_buckets) {
    const buckets = filters.age_buckets.split(',').filter(Boolean);
    buckets.forEach(bucket => {
      tags.push({
        key: 'age_buckets',
        label: `${t('filter.age', 'Age')}: ${AGE_BUCKET_LABELS[bucket] || bucket}`,
        value: bucket,
        isMulti: true
      });
    });
  }

  // Price range
  if (filters.min_price) {
    tags.push({
      key: 'min_price',
      label: `${t('filter.price', 'Price')} ≥ €${filters.min_price}`,
      value: filters.min_price
    });
  }
  if (filters.max_price) {
    tags.push({
      key: 'max_price',
      label: `${t('filter.price', 'Price')} ≤ €${filters.max_price}`,
      value: filters.max_price
    });
  }

  // Handle removal of a single filter
  const handleRemove = (tag) => {
    if (tag.isMulti) {
      // For multi-select filters, remove just this value
      const currentValues = filters[tag.key]?.split(',').filter(Boolean) || [];
      const newValues = currentValues.filter(v => v !== tag.value);
      onRemoveFilter(tag.key, newValues.join(',') || '');
    } else {
      // For single-value filters, clear the filter
      onRemoveFilter(tag.key, '');
    }
  };

  if (tags.length === 0) return null;

  return (
    <div className={className} data-testid="active-filter-tags">
      <div className="flex flex-wrap items-center gap-2">
        {tags.map((tag, index) => (
          <Badge 
            key={`${tag.key}-${tag.value}-${index}`}
            variant="secondary" 
            className="gap-1 pl-2 pr-1 py-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 transition-colors cursor-default group"
            data-testid={`filter-tag-${tag.key}`}
          >
            <span className="text-xs">{tag.label}</span>
            <button 
              onClick={() => handleRemove(tag)}
              className="ml-1 p-0.5 rounded-full hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
              aria-label={`Remove ${tag.label} filter`}
              data-testid={`remove-filter-${tag.key}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        {tags.length > 1 && (
          <button
            onClick={onClearAll}
            className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors"
            data-testid="clear-all-filters"
          >
            {t('filter.clearAll', 'Clear all')}
          </button>
        )}
      </div>
    </div>
  );
};

export default ActiveFilterTags;
